import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";

const FIELDS = [
  { key: "security_deposit", label: "Security Deposit ($)", type: "number", step: "1", help: "Refundable deposit charged at booking." },
  { key: "monthly_due_day", label: "Monthly Due Day", type: "number", step: "1", help: "Day of month rent is due (e.g. 1)." },
  { key: "late_fee_pct", label: "Late Fee (%)", type: "number", step: "0.1", help: "Percentage charged after grace period." },
  { key: "grace_period_days", label: "Grace Period (days)", type: "number", step: "1", help: "Days after due-day before late fee applies." },
  { key: "insurance_min", label: "Insurance Minimum ($)", type: "number", step: "100000", help: "Required tenant liability insurance." },
  { key: "termination_notice_days", label: "Termination Notice (days)", type: "number", step: "1", help: "Notice required to end the lease." },
  { key: "governing_law", label: "Governing Law", type: "text", help: "Jurisdiction the agreement is governed by." },
  { key: "landlord_name", label: "Landlord Name", type: "text", help: "Appears on the signature page." },
  { key: "property_address", label: "Property Address", type: "text", help: "Shown at the top of the lease." },
];

export default function LeaseTemplateEditor() {
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/lease-template").then((r) => setTpl(r.data)).catch(() => toast.error("Could not load lease template"));
  }, []);

  if (!tpl) {
    return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;
  }

  const setField = (k, v) => setTpl((t) => ({ ...t, [k]: v }));
  const setClause = (i, patch) => setTpl((t) => {
    const arr = [...(t.custom_clauses || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...t, custom_clauses: arr };
  });
  const addClause = () => setTpl((t) => ({ ...t, custom_clauses: [...(t.custom_clauses || []), { _key: `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, title: "", body: "" }] }));
  const removeClause = (i) => setTpl((t) => ({ ...t, custom_clauses: t.custom_clauses.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...tpl,
        security_deposit: parseFloat(tpl.security_deposit),
        monthly_due_day: parseInt(tpl.monthly_due_day),
        late_fee_pct: parseFloat(tpl.late_fee_pct),
        grace_period_days: parseInt(tpl.grace_period_days),
        insurance_min: parseFloat(tpl.insurance_min),
        termination_notice_days: parseInt(tpl.termination_notice_days),
        custom_clauses: (tpl.custom_clauses || []).filter((c) => c.title?.trim() || c.body?.trim()),
      };
      const { data } = await api.put("/admin/lease-template", payload);
      setTpl(data);
      toast.success("Lease template saved — applies to all new leases");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="lease-template-editor" className="border-2 border-[#0B0C0E] bg-white p-8">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="overline">Lease Agreement Template</div>
          <h2 className="mt-2 font-display font-black text-2xl uppercase tracking-tight text-[#0B0C0E]">Edit lease defaults</h2>
          <p className="mt-2 text-sm text-[#56606E] max-w-xl">
            Changes apply to every new booking and to PDFs regenerated for existing bookings. Existing tenant deposit amounts are locked at the value used when their booking was created.
          </p>
        </div>
        <button
          onClick={save} disabled={saving}
          className="bg-[#0B0C0E] hover:bg-black disabled:opacity-50 text-white font-bold px-6 py-3 uppercase tracking-wide inline-flex items-center gap-2 border-l-4 border-[#FACC15] whitespace-nowrap"
          data-testid="save-template-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Template
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="overline mb-1 block">{f.label}</label>
            <input
              type={f.type} step={f.step}
              value={tpl[f.key] ?? ""}
              onChange={(e) => setField(f.key, e.target.value)}
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-4 py-2.5 text-sm text-[#0B0C0E]"
              data-testid={`template-${f.key}`}
            />
            <p className="mt-1 text-xs text-[#56606E]">{f.help}</p>
          </div>
        ))}
      </div>

      {/* Custom clauses */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Additional Clauses</div>
            <h3 className="mt-1 font-display font-black text-lg uppercase tracking-tight text-[#0B0C0E]">Custom legal clauses</h3>
            <p className="text-xs text-[#56606E] mt-1">Appended after the standard clauses on every lease PDF.</p>
          </div>
          <button onClick={addClause} className="border-2 border-[#0B0C0E] hover:bg-[#FACC15] text-[#0B0C0E] font-bold px-4 py-2 text-xs uppercase inline-flex items-center gap-2" data-testid="add-clause-btn">
            <Plus className="w-3 h-3" /> Add Clause
          </button>
        </div>

        <div className="space-y-4">
          {(tpl.custom_clauses || []).length === 0 && (
            <div className="text-center text-sm text-[#97A1AE] py-8 border border-dashed border-[#C6C3B5]">
              No custom clauses yet. Click &quot;Add Clause&quot; to add one.
            </div>
          )}
          {(tpl.custom_clauses || []).map((c, i) => (
            <div key={c._key || `existing-${i}`} className="border border-[#E5E3DA] p-4 bg-[#FCFBF6]" data-testid={`clause-${i}`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <input
                  value={c.title || ""} placeholder="Clause title (e.g. INDEMNITY)"
                  onChange={(e) => setClause(i, { title: e.target.value })}
                  className="flex-1 bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm font-bold text-[#0B0C0E]"
                  data-testid={`clause-title-${i}`}
                />
                <button onClick={() => removeClause(i)} className="text-red-700 hover:bg-red-100 p-2" data-testid={`remove-clause-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea
                rows={3} value={c.body || ""} placeholder="Clause body text…"
                onChange={(e) => setClause(i, { body: e.target.value })}
                className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                data-testid={`clause-body-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
